export const generateRevenueChartPayload = async (
  inputArray,
  startDate,
  endDate,
  facilityFilter = null,
  units = false
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const isWithinTwoMonths = isLessThanTwoMonths(start, end);
  const resultMap = new Map();
  const dateRange = getDatesInRange(start, end);
  dateRange.forEach((date) => {
    let key;
    if (isWithinTwoMonths) {
      key = date.toLocaleString("en-US", { day: "2-digit", month: "short" });
    } else {
      key = isWithinTwoMonths
        ? date.toISOString().split("T")[0]
        : `${date.toLocaleString("en-US", {
            month: "short",
          })}-${date.getFullYear()}`;
    }
    const obj = units
      ? { created_at: key, units_consumed: 0 }
      : { created_at: key, total_amount_spent: 0 };
    resultMap.set(key, obj);
  });

  inputArray.forEach(
    ({
      created_at,
      total_amount_spent,
      units_consumed,
      meter_start_at,
      meter_stop_at,
      power_type,
      penalty_fee,
      penalty_paid,
      payment_paid,
      penalty_paid_at,
      payment_paid_at,
    }) => {
      const currentDate = new Date(created_at);
      let paymentDate = new Date(created_at);
      if (payment_paid_at > 0) {
        paymentDate = new Date(0);
        paymentDate.setUTCSeconds(payment_paid_at);
      }
      let penaltyPaidDate = new Date(created_at);
      if (penalty_paid_at > 0) {
        penaltyPaidDate = new Date(0);
        penaltyPaidDate.setUTCSeconds(penalty_paid_at);
      }

      if (units && currentDate >= start && currentDate <= end) {
        const key = isWithinTwoMonths
          ? currentDate.toLocaleString("en-US", {
              day: "2-digit",
              month: "short",
            })
          : `${currentDate.toLocaleString("en-US", {
              month: "short",
            })}-${currentDate.getFullYear()}`;
        if (resultMap.has(key)) {
          resultMap.get(key).units_consumed +=
            calculateUnitsConsumed(
              units_consumed,
              meter_start_at,
              meter_stop_at,
              power_type,
              facilityFilter,
              false
            ) / 1000;
        } else {
          resultMap.set(key, {
            created_at: key,
            units_consumed:
              calculateUnitsConsumed(
                units_consumed,
                meter_start_at,
                meter_stop_at,
                power_type,
                facilityFilter,
                false
              ) / 1000,
          });
        }
      }

      if (!units && paymentDate >= start && paymentDate <= end) {
        const key = isWithinTwoMonths
          ? paymentDate.toLocaleString("en-US", {
              day: "2-digit",
              month: "short",
            })
          : `${paymentDate.toLocaleString("en-US", {
              month: "short",
            })}-${paymentDate.getFullYear()}`;
        if (resultMap.has(key)) {
          const chargingPaidAmount = payment_paid
            ? penalty_paid
              ? total_amount_spent - penalty_fee
              : total_amount_spent
            : 0;
          resultMap.get(key).total_amount_spent += chargingPaidAmount;
        } else {
          resultMap.set(key, { created_at: key, total_amount_spent });
        }
      }

      if (!units && penaltyPaidDate >= start && penaltyPaidDate <= end) {
        const key = isWithinTwoMonths
          ? paymentDate.toLocaleString("en-US", {
              day: "2-digit",
              month: "short",
            })
          : `${penaltyPaidDate.toLocaleString("en-US", {
              month: "short",
            })}-${penaltyPaidDate.getFullYear()}`;
        if (resultMap.has(key)) {
          const penaltyPaidAmount = penalty_paid ? penalty_fee : 0;
          resultMap.get(key).total_amount_spent += penaltyPaidAmount;
        } else {
          resultMap.set(key, { created_at: key, total_amount_spent });
        }
      }
    }
  );

  const resultArray = Array.from(resultMap.values()).sort((a, b) => {
    const dateA = isWithinTwoMonths
      ? new Date().setDate(1, a.created_at)
      : new Date(a.created_at);
    const dateB = isWithinTwoMonths
      ? new Date().setDate(1, b.created_at)
      : new Date(b.created_at);
    return dateA - dateB;
  });

  return resultArray;
};

export const isLessThanTwoMonths = (start, end) => {
  const diffInYears = end.getFullYear() - start.getFullYear();
  const diffInMonths = end.getMonth() - start.getMonth() + diffInYears * 12;
  return diffInMonths < 2;
};

const calculateProfit = (facilityFilter) => [];

export const generateTransactionChartPayload = async (
  transactions,
  startDate,
  endDate,
  is_fleet = false,
  facilityFilter = null
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const oneMonthInMillis = isLessThanTwoMonths(start, end);
  const datesInRange = getDatesInRange(start, end);

  if (oneMonthInMillis) {
    const result = datesInRange.reduce((acc, date) => {
      const key = formatDate(date);
      const dailyTransactions = transactions.filter(
        (transaction) => formatDate(new Date(transaction.created_at)) === key
      );
      const count = dailyTransactions.length;
      if (!is_fleet) acc.push({ created_at: key, count });
      else {
        const revenue =
          (is_fleet
            ? dailyTransactions.reduce(
                (sum, transaction) =>
                  sum +
                  calculateUnitsConsumed(
                    transaction.units_consumed,
                    transaction.meter_start_at,
                    transaction.meter_stop_at,
                    transaction.power_type,
                    facilityFilter
                  ),
                0
              )
            : 0) / 1000;
        const units_consumed =
          (is_fleet
            ? dailyTransactions.reduce(
                (sum, transaction) =>
                  sum +
                  calculateUnitsConsumed(
                    transaction.units_consumed,
                    transaction.meter_start_at,
                    transaction.meter_stop_at,
                    transaction.power_type,
                    facilityFilter,
                    false
                  ),
                0
              )
            : 0) / 1000;
        const electricity = Number(
          (units_consumed * facilityFilter?.price_per_unit_electricity).toFixed(
            2
          )
        );
        const profit = revenue - electricity;
        const company_profit = Number(
          checkCompanyProfit(facilityFilter, units_consumed, profit).toFixed(2)
        );
        acc.push({
          created_at: key,
          count,
          revenue: revenue,
          profit: profit - company_profit,
          company_profit,
          electricity,
          units_consumed,
        });
      }
      return acc;
    }, []);

    return result.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  } else {
    const result = transactions.reduce((acc, transaction) => {
      const date = new Date(transaction.created_at);
      const key = `${getMonthName(date.getMonth())}-${date.getFullYear()}`;
      acc[key] = acc[key] || {
        count: 0,
        units_consumed: 0,
        revenue: 0,
        electricity: 0,
        company_profit: 0,
        profit: 0,
      };
      acc[key].count += 1;
      if (is_fleet) {
        acc[key].revenue +=
          calculateUnitsConsumed(
            transaction.units_consumed,
            transaction.meter_start_at,
            transaction.meter_stop_at,
            transaction.power_type,
            facilityFilter
          ) / 1000;
        acc[key].units_consumed +=
          calculateUnitsConsumed(
            transaction.units_consumed,
            transaction.meter_start_at,
            transaction.meter_stop_at,
            transaction.power_type,
            facilityFilter,
            false
          ) / 1000;
        acc[key].electricity = Number(
          (
            acc[key].units_consumed *
            (facilityFilter?.price_per_unit_electricity ?? 0)
          ).toFixed(2)
        );
        const profit = acc[key].revenue - acc[key].electricity;
        acc[key].company_profit = Number(
          checkCompanyProfit(
            facilityFilter,
            acc[key].units_consumed,
            profit
          ).toFixed(2)
        );
        acc[key].profit = profit - acc[key].company_profit;
      }
      return acc;
    }, {});

    const allMonths = generateAllMonthsInRange(start, end);

    let finalResult = allMonths.map((month) => ({
      created_at: month,
      count: result[month] ? result[month].count : 0,
      revenue: is_fleet ? (result[month] ? result[month].revenue : 0) : 0,
      electricity: is_fleet
        ? result[month]
          ? result[month].electricity
          : 0
        : 0,
      company_profit: is_fleet
        ? result[month]
          ? result[month].company_profit
          : 0
        : 0,
      profit: is_fleet ? (result[month] ? result[month].profit : 0) : 0,
    }));

    return finalResult.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  }
};

function givePrice(power_type, facilityFilter) {
  if (power_type === "AC") return facilityFilter?.price_per_unit_ac ?? 0;
  else return facilityFilter?.price_per_unit_dc ?? 0;
}

const checkCompanyProfit = (facilityFilter, units_consumed, profit) => {
  if (facilityFilter?.is_profit_sharing) {
    if (facilityFilter?.profit_sharing_type === "Fixed")
      return units_consumed * (facilityFilter?.profit_sharing_amount ?? 0);
    return (profit * (facilityFilter?.profit_sharing_amount ?? 0)) / 100;
  }
  return 0;
};

function formatDate(date) {
  const day =
    getMonthName(date.getMonth()).substring(0, 3) +
    " " +
    date.getDate().toString().padStart(2, "0");
  return `${day}`;
}

export function calculateUnitsConsumed(
  units,
  start,
  stop,
  power_type,
  facilityFilter,
  includePrice = true
) {
  if (units >= 0)
    return includePrice ? units * givePrice(power_type, facilityFilter) : units;
  else if (start && stop && stop > start) {
    return includePrice
      ? (stop - start) * givePrice(power_type, facilityFilter)
      : stop - start;
  } else return 0;
}

export function getMonthName(monthIndex) {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return monthNames[monthIndex];
}

const getDatesInRange = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

function generateAllMonthsInRange(start, end) {
  const allMonths = [];
  const currentDate = new Date(start);

  while (currentDate <= end) {
    const key = `${getMonthName(
      currentDate.getMonth()
    )}-${currentDate.getFullYear()}`;
    allMonths.push(key);
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return allMonths;
}
